import { useState, useEffect, useRef, useCallback, memo } from "react";
import { ref, onValue, push, set } from "firebase/database";
import { db } from "../../functions/api_firebase";
import AnimacionCarga from "./animacion_de_carga";
import BarraSuperior from "./barra_superior";
import BotonSOS from "./sos/boton_sos";
import Swal from "sweetalert2";
import { subirArchivoFirebase } from "../../functions/archivos_firebase";
import { fechaHoraDart, parsearFechaPase, fechaHora12Dart } from "../../functions/pase_visitante";
import { CONFIG } from "../../functions/variables_globales";
import "bootstrap-icons/font/bootstrap-icons.css";


const rutaCanales = () => `coordi_ph/${CONFIG.codigoCliente()}/chat`;
const rutaCanal = (canal) => `${rutaCanales()}/${canal}`;

const esResidente = () => localStorage.getItem("Permisos") === "RESIDENTE";

const datosUsuario = () => ({
  correo: localStorage.getItem("CorreoElectronico") || "",
  nombre: localStorage.getItem("NombreCompleto") || localStorage.getItem("CorreoElectronico") || "",
  residencia: localStorage.getItem("Direccion") || "",
});

const formatearFecha = (valor) => {
  const fecha = parsearFechaPase(valor);
  return fecha ? fechaHora12Dart(fecha) : valor || "";
};

const tiempoDe = (valor) => {
  const fecha = parsearFechaPase(valor);
  return fecha ? fecha.getTime() : 0;
};

const normalizarMensajes = (datos) =>
  Object.entries(datos || {})
    .map(([id, m]) => ({
      id,
      fecha: m?.fecha || "",
      mensaje: m?.mensaje || "",
      usuario: m?.usuario || "",
      tipo: m?.tipo || "Mensaje",
      url: m?.url || "",
      casa: m?.casa || "",
      nombreUsuario: m?.nombreUsuario || "",
    }))
    .sort((a, b) => tiempoDe(a.fecha) - tiempoDe(b.fecha));

const resumirCanales = (datos) =>
  Object.entries(datos || {})
    .map(([canal, mensajes]) => {
      const lista = normalizarMensajes(mensajes);
      const ultimo = lista[lista.length - 1];
      return {
        canal,
        cantidad: lista.length,
        ultimoTexto: ultimo ? (ultimo.tipo === "Mensaje" ? ultimo.mensaje : `[${ultimo.tipo}]`) : "",
        ultimaFecha: ultimo?.fecha || "",
        orden: ultimo ? tiempoDe(ultimo.fecha) : 0,
      };
    })
    .sort((a, b) => b.orden - a.orden);


const ListaCanales = ({ canales, seleccionado, onSeleccionar, filtro, onFiltrar }) => (
  <div style={s.panelIzquierdo}>
    <div style={s.buscadorWrap}>
      <i className="bi bi-search" style={s.buscadorIcono} />
      <input
        style={s.buscadorInput}
        value={filtro}
        onChange={(e) => onFiltrar(e.target.value)}
        placeholder="Buscar residencia"
      />
    </div>

    <div style={s.listaCanales}>
      {canales.length === 0 ? (
        <div style={s.vacioLista}>No hay conversaciones.</div>
      ) : (
        canales.map((c) => {
          const activo = c.canal === seleccionado;
          return (
            <button
              key={c.canal}
              type="button"
              onClick={() => onSeleccionar(c.canal)}
              style={{ ...s.itemCanal, ...(activo ? s.itemCanalActivo : {}) }}
            >
              <div style={s.itemCanalIcono}><i className="bi bi-chat-dots" /></div>
              <div style={s.itemCanalTexto}>
                <span style={s.itemCanalTitulo}>{c.canal}</span>
                <span style={s.itemCanalUltimo}>{c.ultimoTexto || "Sin mensajes"}</span>
              </div>
              <span style={s.itemCanalBadge}>{c.cantidad}</span>
            </button>
          );
        })
      )}
    </div>
  </div>
);


const Burbuja = ({ mensaje, correoPropio }) => {
  const propio = mensaje.usuario === correoPropio;

  return (
    <div style={{ ...s.filaBurbuja, justifyContent: propio ? "flex-end" : "flex-start" }}>
      <div style={{ maxWidth: "70%" }}>
        <span style={{ ...s.autor, textAlign: propio ? "right" : "left" }}>
          {propio ? "Yo" : mensaje.nombreUsuario || mensaje.usuario}
        </span>

        <div style={{ ...s.burbuja, ...(propio ? s.burbujaPropia : s.burbujaAjena) }}>
          {mensaje.tipo === "Imagen" && mensaje.url ? (
            <a href={mensaje.url} target="_blank" rel="noopener noreferrer">
              <img src={mensaje.url} alt="Adjunto" style={s.imagen} />
            </a>
          ) : mensaje.tipo === "Audio" && mensaje.url ? (
            <audio controls src={mensaje.url} style={{ maxWidth: 240 }}>
              Su navegador no reproduce audio.
            </audio>
          ) : (
            <span style={s.textoMensaje}>{mensaje.mensaje}</span>
          )}
        </div>

        <span style={{ ...s.hora, textAlign: propio ? "right" : "left" }}>
          {formatearFecha(mensaje.fecha)}
        </span>
      </div>
    </div>
  );
};


const ListadoChats = memo(() => {
  const [canales, setCanales] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState("");
  const [filtro, setFiltro] = useState("");
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);

  const mensajesRef = useRef(null);
  const archivoRef = useRef(null);
  const usuario = datosUsuario();

  useEffect(() => {
    const refCanales = ref(db, rutaCanales());

    const cancelar = onValue(
      refCanales,
      (snap) => {
        const datos = snap.val() || {};
        const resumen = resumirCanales(datos);

        const visibles = esResidente()
          ? resumen.filter((c) => c.canal === usuario.residencia)
          : resumen;

        setCanales(visibles);
        setCargando(false);
      },
      (error) => {
        console.error("Error al escuchar chats:", error);
        setCargando(false);
        Swal.fire({ icon: "error", title: "Error", text: "No se pudieron cargar los chats." });
      }
    );

    return () => cancelar();
  }, [usuario.residencia]);

  useEffect(() => {
    if (!seleccionado && esResidente() && usuario.residencia) {
      setSeleccionado(usuario.residencia);
    }
  }, [seleccionado, usuario.residencia]);

  useEffect(() => {
    if (!seleccionado) {
      setMensajes([]);
      return undefined;
    }

    const cancelar = onValue(
      ref(db, rutaCanal(seleccionado)),
      (snap) => setMensajes(normalizarMensajes(snap.val())),
      (error) => console.error("Error al escuchar el canal:", error)
    );

    return () => cancelar();
  }, [seleccionado]);

  useEffect(() => {
    const caja = mensajesRef.current;
    if (caja) caja.scrollTop = caja.scrollHeight;
  }, [mensajes]);

  const guardarMensaje = useCallback(async (contenido, tipo, url = "") => {
    const nuevo = push(ref(db, rutaCanal(seleccionado)));
    await set(nuevo, {
      fecha: fechaHoraDart(),
      mensaje: contenido,
      usuario: usuario.correo,
      tipo,
      url,
      casa: seleccionado,
      nombreUsuario: usuario.nombre,
    });
  }, [seleccionado, usuario.correo, usuario.nombre]);

  const enviarTexto = useCallback(async () => {
    const contenido = texto.trim();
    if (!contenido || !seleccionado || enviando) return;

    setEnviando(true);
    setTexto("");

    try {
      await guardarMensaje(contenido, "Mensaje");
    } catch (e) {
      console.error(e);
      setTexto(contenido);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo enviar el mensaje." });
    } finally {
      setEnviando(false);
    }
  }, [texto, seleccionado, enviando, guardarMensaje]);

  const enviarImagen = useCallback(async (evento) => {
    const archivo = evento.target.files?.[0];
    evento.target.value = "";
    if (!archivo || !seleccionado) return;

    if (!archivo.type.startsWith("image/")) {
      Swal.fire({ icon: "warning", title: "Formato no válido", text: "Seleccione una imagen." });
      return;
    }

    Swal.fire({
      title: "Enviando imagen",
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      const extension = archivo.name.split(".").pop() || "jpg";
      const nombre = `Foto - ${fechaHoraDart()}`;
      const url = await subirArchivoFirebase(
        `chat/${CONFIG.codigoCliente()}/${nombre}.${extension}`,
        archivo
      );

      await guardarMensaje("", "Imagen", url);
      Swal.close();
    } catch (e) {
      console.error(e);
      Swal.fire({ icon: "error", title: "Error", text: "No se pudo enviar la imagen." });
    }
  }, [seleccionado, guardarMensaje]);

  if (cargando) return <AnimacionCarga />;

  const canalesFiltrados = filtro.trim()
    ? canales.filter((c) => c.canal.toLowerCase().includes(filtro.trim().toLowerCase()))
    : canales;

  const puedeEscribir = Boolean(seleccionado);

  return (
    <div style={s.page}>
      <BarraSuperior />

      <div style={s.contenedor}>
        <header style={s.header}>
          <div style={s.headerTexto}>
            <h1 style={s.headerTitle}>Ver chats</h1>
            <p style={s.headerSub}>
              Conversaciones por residencia de la copropiedad.
            </p>
          </div>

          <div style={s.headerAcciones}>
            <span style={s.chip}>
              <i className="bi bi-chat-dots" />
              {canales.length} conversacion{canales.length === 1 ? "" : "es"}
            </span>
            <button type="button" style={s.botonIcono} onClick={() => window.location.reload()} title="Refrescar">
              <i className="bi bi-arrow-clockwise" />
            </button>
          </div>
        </header>

        <div style={s.tablero}>
          {!esResidente() && (
            <ListaCanales
              canales={canalesFiltrados}
              seleccionado={seleccionado}
              onSeleccionar={setSeleccionado}
              filtro={filtro}
              onFiltrar={setFiltro}
            />
          )}

        <div style={s.panelDerecho}>
          {!seleccionado ? (
            <div style={s.vacioConversacion}>
              <i className="bi bi-chat-square-text" style={{ fontSize: 34, color: "#d1d5db" }} />
              <p style={{ marginTop: 12 }}>Seleccione una conversación para ver los mensajes.</p>
            </div>
          ) : (
            <>
              <div style={s.tituloConversacion}>
                <i className="bi bi-house-door" style={{ flexShrink: 0 }} />
                <span style={s.tituloConversacionTexto}>{seleccionado}</span>
              </div>

              <div style={s.mensajes} ref={mensajesRef}>
                {mensajes.length === 0 ? (
                  <div style={s.vacioConversacion}>Aún no hay mensajes en esta conversación.</div>
                ) : (
                  mensajes.map((m) => (
                    <Burbuja key={m.id} mensaje={m} correoPropio={usuario.correo} />
                  ))
                )}
              </div>

              <div style={s.barraEnvio}>
                <input
                  ref={archivoRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={enviarImagen}
                />
                <button
                  type="button"
                  style={s.botonAdjuntar}
                  onClick={() => archivoRef.current?.click()}
                  disabled={!puedeEscribir}
                  title="Adjuntar imagen"
                >
                  <i className="bi bi-image" />
                </button>

                <input
                  style={s.inputMensaje}
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") enviarTexto(); }}
                  placeholder="Escribe un mensaje"
                  disabled={!puedeEscribir}
                />

                <button
                  type="button"
                  style={s.botonEnviar}
                  onClick={enviarTexto}
                  disabled={!puedeEscribir || !texto.trim() || enviando}
                  title="Enviar"
                >
                  <i className="bi bi-send" />
                </button>
              </div>
            </>
          )}
          </div>
        </div>
      </div>

      <BotonSOS />
    </div>
  );
});

const s = {
  page: {
    minHeight: "100vh",
    background: "#f8f9fb",
    fontFamily: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
    paddingBottom: 100,
  },
  contenedor: { width: "100%", maxWidth: 1600, margin: "0 auto", padding: "0 32px", boxSizing: "border-box" },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 16,
    padding: "28px 0 20px",
    borderBottom: "1px solid #e5e7eb",
    marginBottom: 24,
    minWidth: 0,
  },
  headerTexto: { minWidth: 0, flex: "1 1 320px" },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: 700, color: "#1a1a2e", lineHeight: 1.25 },
  headerSub: { margin: "6px 0 0", fontSize: 13.5, color: "#6b7280" },
  headerAcciones: { display: "flex", alignItems: "center", gap: 12, flexShrink: 0 },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 12px",
    borderRadius: 999,
    background: "#ecfdf5",
    color: "#047857",
    fontSize: 12.5,
    fontWeight: 600,
  },
  botonIcono: {
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: "1px solid #d1d5db",
    background: "#ffffff",
    color: "#6b7280",
    fontSize: 16,
    cursor: "pointer",
  },
  tablero: { display: "flex", gap: 24, alignItems: "stretch", flexWrap: "wrap" },
  panelIzquierdo: {
    flex: "1 1 300px",
    minWidth: 260,
    maxWidth: 380,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  buscadorWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    padding: 14,
    borderBottom: "1px solid #f0f1f4",
  },
  buscadorIcono: { position: "absolute", left: 27, color: "#9ca3af", fontSize: 13 },
  buscadorInput: {
    width: "100%",
    padding: "11px 13px 11px 34px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14,
    color: "#1a1a2e",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  listaCanales: { overflowY: "auto", overflowX: "hidden", flex: 1, maxHeight: 560, padding: "8px 0" },
  itemCanal: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "13px 16px",
    border: "none",
    borderLeft: "3px solid transparent",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
  },
  itemCanalActivo: { background: "#fef6f5", borderLeftColor: "#c0392b" },
  itemCanalIcono: { color: "#c0392b", fontSize: 17, flexShrink: 0 },
  itemCanalTexto: { flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2, overflow: "hidden" },
  itemCanalTitulo: {
    fontSize: 14,
    fontWeight: 600,
    color: "#1a1a2e",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemCanalUltimo: {
    fontSize: 12.5,
    color: "#9ca3af",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  itemCanalBadge: {
    flexShrink: 0,
    fontSize: 11.5,
    fontWeight: 600,
    color: "#6b7280",
    background: "#f3f4f6",
    borderRadius: 999,
    padding: "2px 9px",
  },
  panelDerecho: {
    flex: "3 1 480px",
    minWidth: 320,
    minHeight: 560,
    background: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: 14,
    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  tituloConversacion: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "16px 20px",
    borderBottom: "1px solid #f0f1f4",
    fontSize: 15,
    fontWeight: 700,
    color: "#1a1a2e",
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  tituloConversacionTexto: {
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  mensajes: { flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden", padding: 20, background: "#f8f9fb" },
  filaBurbuja: { display: "flex", marginBottom: 14 },
  autor: { display: "block", fontSize: 11.5, color: "#9ca3af", marginBottom: 4 },
  burbuja: { borderRadius: 12, padding: "10px 14px" },
  burbujaPropia: { background: "#c0392b", color: "#ffffff" },
  burbujaAjena: { background: "#ffffff", color: "#1a1a2e", border: "1px solid #e5e7eb" },
  textoMensaje: { fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  imagen: { maxWidth: 260, borderRadius: 8, display: "block" },
  hora: { display: "block", fontSize: 10.5, color: "#b6bcc6", marginTop: 4 },
  barraEnvio: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: 14,
    borderTop: "1px solid #f0f1f4",
  },
  inputMensaje: {
    flex: 1,
    minWidth: 0,
    padding: "11px 14px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14,
    color: "#1a1a2e",
    outline: "none",
    fontFamily: "inherit",
  },
  botonAdjuntar: {
    flexShrink: 0,
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #d1d5db",
    background: "#ffffff",
    borderRadius: 10,
    cursor: "pointer",
    color: "#6b7280",
    fontSize: 16,
  },
  botonEnviar: {
    flexShrink: 0,
    width: 42,
    height: 42,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "#c0392b",
    color: "#ffffff",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 16,
  },
  vacioLista: { padding: 28, textAlign: "center", color: "#9ca3af", fontSize: 13.5 },
  vacioConversacion: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "#9ca3af",
    fontSize: 14,
    padding: 40,
    textAlign: "center",
  },
};

export default ListadoChats;
